/**
 * A service that carries its parts is a BUNDLE.
 *
 * The customer sees one line — the service name and one amount covering the
 * labor plus everything folded into it. Internally the parts stay real lines so
 * cost, margin, GL and purchasing still see them individually.
 *
 * The part that is easy to get wrong: whether the parts are shown separately is
 * not a cosmetic choice. Stating each part on the customer's document is
 * precisely what takes a sale OUT of bundling — SSUTA excludes items itemized
 * on sales documentation, and states such as Michigan and Nevada hang
 * taxability on separate statement. So:
 *
 *   ROLLED_UP  → one price → one taxable base, on the SERVICE's own treatment
 *   ITEMIZED   → separately stated → each line taxed on its own treatment
 *
 * Services derive to `labor`, so materials folded into a service are never
 * taxed as materials. That is a classification, not an exemption: each
 * jurisdiction's own codes decide what labor costs.
 *
 * Mirrors `resolveBundles` in operations' pricing engine. If these two ever
 * disagree the operator's preview lies about tax, so they change together.
 */
import type { TaxLineType } from '@/types/tax';
import { chargesRevenue, isChargeable, type LineEconomics } from '@/lib/sales/line-economics';
import { resolveLineTaxLineType } from '@/lib/tax/tax-utils';
import {
  groupComponentLines,
  type ComponentGroup,
  type ComponentGroupableLine,
} from './service-components';

/** Explains what happens to tax when the display mode changes. */
/**
 * What each display mode decides — which is TAX, and only that.
 *
 * Two things these must not claim, because the mode does not settle either.
 * NOT the document's shape: a rolled-up bundle still unfolds a visible billed
 * part onto its own row, and a hidden service prints none — the card states
 * the actual row count beside this. NOT what is CHARGED: an included part is
 * inside the service's price under both modes, and a billed one bills on its
 * own line under both. What the mode really governs is whether the lines are
 * STATED as one bundle, and therefore whether they are taxed as one — see
 * `resolveBundles`, which collapses the tax base for ROLLED_UP parents alone.
 */
export const DISPLAY_MODE_TAX_HINT = {
  ROLLED_UP:
    'The service and its parts are stated as one bundle, so they are taxed together on the service’s own classification.',
  ITEMIZED:
    'The parts are stated separately, which takes this out of bundling, so every line is taxed on its own treatment.',
} as const;

/**
 * The tax treatment of a bundle, decided by what is IN it.
 *
 * Mirrors `resolveBundleTaxLineType` in operations' pricing engine. One service
 * anywhere makes the whole taxable base labor — the customer bought an outcome
 * for one price, not a service plus some materials — and its parts ride along,
 * because they have no line of their own to be taxed on. A bundle with no
 * service in it is a kit of parts, so it stays materials.
 *
 * The parent's own `taxLineType` is deliberately NOT consulted: a service line
 * carrying `materials` must not drag its parts onto a goods treatment. What the
 * members ARE decides.
 *
 * Callers must leave unselected parts out — offered is not sold.
 */
export function resolveBundleTaxLineType(
  members: readonly { type?: string | null }[],
): TaxLineType {
  const hasService = members.some((member) => {
    const type = String(member.type ?? '')
      .trim()
      .toUpperCase();
    // An ABSENT type reads as a service here, the same way `canCarryComponents`
    // in service-components.ts treats it: `type` is optional across four
    // different line shapes on this side, and a document whose payload omits it
    // has always rendered — and been taxed — as the service bundle it is.
    // Reading silence as "not a service" would flip those to a goods treatment.
    // The engine has no such case: `PricingLineInput.type` is required there.
    return type === '' || type === 'SERVICE';
  });
  return hasService ? 'labor' : 'materials';
}

export interface BundleEconomics {
  /** The service's own amount — labor, before any parts. 0 when it isn't sold. */
  serviceAmount: number;
  /** Everything folded in that the customer is actually buying. */
  partsAmount: number;
  /** What the customer pays for the bundle, before tax. */
  bundleTotal: number;
  /** partsAmount / bundleTotal, 0 when the bundle is free. */
  materialsShare: number;
  /** Parts that are offered but not selected — excluded from every figure. */
  unselectedPartsAmount: number;
  /**
   * Is the SERVICE itself being sold? False when the parent line is
   * deselected — what is left is standalone part sales, not a bundle.
   */
  isSold: boolean;
  /** The service's own amount when it is NOT sold; excluded from every figure. */
  unselectedServiceAmount: number;
  /**
   * Of `partsAmount`, the parts billed on a line of their OWN — what the
   * header names "Billed parts". An included part's figure (a legacy snapshot
   * can carry one) is inside the service's price and stays in `partsAmount`
   * for the share and the drawer, but is not an operand beside the service.
   */
  billedPartsAmount: number;
  /** `serviceAmount + billedPartsAmount`: the subtotal the header foots to. */
  billedBundleTotal: number;
}

/**
 * Does this part of a bundle bill the customer on a line of its OWN?
 *
 * The relationship's treatment is the answer when it states one — it is what
 * the pricebook editor writes, for a bundle's SERVICE members as much as for
 * its parts — and `chargesRevenue` decides the rest. Read this way a legacy
 * snapshot that carries the treatment but not the line's `type` still keeps an
 * included part inside the service's price instead of being billed beside it
 * (for `chargesRevenue` a line of no type is not a part, hence always billed).
 */
export function componentBillsOwnLine(component: ComponentGroupableLine): boolean {
  if (component.pricingTreatment === 'included_in_service') return false;
  if (component.pricingTreatment === 'separate_charge') return true;
  // No treatment stated: the catalog flag is the default it stands for
  // (`pricingTreatmentOf`). Asked through `chargesRevenue` a line with no
  // `type` is "not a part" and therefore always billed, which is right for a
  // SERVICE and wrong for the legacy component snapshots that state neither
  // field — a part carrying `chargeable: false` was counted beside the
  // service it is included in.
  if (!component.type) return isChargeable(component as LineEconomics);
  return chargesRevenue(component as LineEconomics);
}

/**
 * What the bundle is worth and how much of it is parts.
 *
 * Unselected optional parts are offered, not sold: they contribute to neither
 * the price nor the materials share, or a shop that lists ten optional add-ons
 * would look like it sells nothing but materials.
 *
 * The service itself is no different, and it takes its parts with it: a
 * deselected service over a still-selected part is one click away, and the
 * pricing engine prices that part at nothing precisely because the service it
 * belongs to is not being sold (`declineParts`). These figures say the same
 * thing, or the card would describe a bundle the customer is never sold.
 */
export function bundleEconomics<T extends ComponentGroupableLine>(
  group: ComponentGroup<T>,
  amountOf: (line: T) => number = (line) => line.lineSubtotal ?? 0,
): BundleEconomics {
  const isSold = group.parent.isSelected !== false;
  const parentAmount = amountOf(group.parent);
  const serviceAmount = isSold ? parentAmount : 0;
  let partsAmount = 0;
  let billedPartsAmount = 0;
  let unselectedPartsAmount = 0;

  for (const component of group.components) {
    const amount = amountOf(component);
    // A declined service takes its parts with it — included AND billed extra,
    // lockstep with the engine's `declineParts` (#2452) — so nothing under an
    // unsold parent counts as sold.
    if (!isSold || component.isSelected === false) {
      unselectedPartsAmount += amount;
      continue;
    }
    partsAmount += amount;
    // A part INCLUDED in the service's price bills nothing of its own — its
    // money is inside `serviceAmount` already — so it is no operand beside
    // the service, whatever figure its row still carries.
    if (componentBillsOwnLine(component)) billedPartsAmount += amount;
  }

  const bundleTotal = serviceAmount + partsAmount;
  return {
    serviceAmount,
    partsAmount,
    bundleTotal,
    materialsShare: bundleTotal > 0 ? partsAmount / bundleTotal : 0,
    unselectedPartsAmount,
    isSold,
    unselectedServiceAmount: isSold ? 0 : parentAmount,
    billedPartsAmount,
    billedBundleTotal: serviceAmount + billedPartsAmount,
  };
}

export type BundleWarningLevel = 'none' | 'caution' | 'high';

export interface BundleTaxWarning {
  level: BundleWarningLevel;
  title: string;
  body: string;
}

/**
 * Materials share at which taxing a bundle as pure labor starts to look
 * unsupportable. Not a statutory number — the statutory de minimis tests run
 * the other way (Texas 5%, most SSUTA states 10%, for when the TAXABLE part is
 * negligible). This is the practical inverse: once parts are most of the
 * price, "the customer is buying a service" is the weaker reading of the sale.
 */
const MATERIALS_CAUTION_SHARE = 0.5;
const MATERIALS_HIGH_SHARE = 0.75;

/**
 * Flags a bundle whose tax treatment sits badly with what it is mostly made of.
 *
 * Only fires for treatments that claim the sale is a service (`labor`,
 * `exempt`), and only on a rolled-up bundle that is actually being sold — an
 * itemized one states its parts and taxes them on their own, and a deselected
 * service makes no claim about the sale at all, so neither has anything to
 * question.
 *
 * This is a prompt to look, not a blocker. The right answer depends on the
 * jurisdiction and on whether the work is a capital improvement, and neither is
 * something this screen can decide.
 */
export function bundleTaxWarning(args: {
  isRolledUp: boolean;
  /** The SERVICE's own treatment — it governs the whole bundle. */
  treatment: TaxLineType | null | undefined;
  economics: Pick<BundleEconomics, 'materialsShare' | 'partsAmount'> &
    Partial<Pick<BundleEconomics, 'isSold'>>;
  hasParts: boolean;
}): BundleTaxWarning | null {
  const { isRolledUp, treatment, economics, hasParts } = args;
  if (!isRolledUp || !hasParts) return null;
  if (economics.isSold === false) return null;
  if (treatment !== 'labor' && treatment !== 'exempt') return null;
  if (economics.materialsShare < MATERIALS_CAUTION_SHARE) return null;

  const percent = Math.round(economics.materialsShare * 100);
  const asExempt = treatment === 'exempt';
  const level: BundleWarningLevel =
    economics.materialsShare >= MATERIALS_HIGH_SHARE ? 'high' : 'caution';

  return {
    level,
    title: `Parts are ${percent}% of this bundle`,
    body: asExempt
      ? `This bundle is being sold tax-exempt, but most of its price is materials. Exemption usually rests on the work being a capital improvement or the customer being exempt — not on the parts sitting inside a service. Worth confirming before this goes out.`
      : `The whole bundle is taxed as labor, which assumes the customer is buying the work rather than the parts. At ${percent}% materials that reading is hard to defend in most states${
          level === 'high' ? ', and this is well past the point where it usually fails' : ''
        }. Either reclassify the service itself in the pricebook, or show the parts separately so each is taxed on its own.`,
  };
}

/**
 * The classification the ENGINE will charge this bundle on.
 *
 * Same precedence as `calculateLineTaxV2`: the parent's own `exempt` wins, and
 * only then does the bundle's derived treatment apply. Unselected parts are
 * offered rather than sold, so they do not get a vote.
 */
function bundleWarningTreatment<T extends ComponentGroupableLine & { taxLineType?: string | null }>(
  group: ComponentGroup<T>,
): TaxLineType {
  const parentOwn = resolveLineTaxLineType(group.parent);
  if (parentOwn === 'exempt') return 'exempt';
  return resolveBundleTaxLineType([
    group.parent,
    ...group.components.filter((component) => component.isSelected !== false),
  ]);
}

/** Convenience: economics + warning for every bundle in a flat line list. */
export function analyzeBundles<T extends ComponentGroupableLine & { taxLineType?: string | null }>(
  lines: T[],
  amountOf?: (line: T) => number,
): Array<{
  group: ComponentGroup<T>;
  economics: BundleEconomics;
  warning: BundleTaxWarning | null;
}> {
  return groupComponentLines(lines).map((group) => {
    const economics = bundleEconomics(group, amountOf);
    return {
      group,
      economics,
      warning: bundleTaxWarning({
        isRolledUp: group.isRolledUp,
        // The treatment the ENGINE will use, derived from what is in the
        // bundle — not the parent's stored value. Reading the snapshot meant a
        // service carrying `materials` was taxed as labor and warned about as
        // materials, which suppressed the caution in exactly the case that
        // needs it: the warning only fires on `labor` and `exempt`.
        //
        // Exempt is checked first, because that is the order the engine
        // resolves in: a parent marked exempt is untaxed whatever the bundle
        // would otherwise be, and that is the case the exempt wording is for.
        treatment: bundleWarningTreatment(group),
        economics,
        hasParts: group.components.length > 0,
      }),
    };
  });
}
